// F17 拡張: Meshy API による 3D キャラ自動生成（計画書§16.11）。
// ③のキャスト画像を入力に、役割別の工程グラフで GLB を生成して推奨配置先へ保存する:
//   プレイアブル（protagonist/partner）= (1)画像→3D(standard/Aポーズ) → (2)テクスチャ → (3)リギング → (4)アニメ×3 → (5)_wait/_move/_btl 保存
//   人型敵                             = (1)画像→3D(Aポーズ) → (2)テクスチャ → (3)リギング → (4)アニメ×2 → idle/walk 統合GLB 1点保存
//   非人型敵                           = (1)画像→3D(自然体) → (2)テクスチャ → 静的GLB 1点 + Motion Profile JSON
//   （敵GLBは本家 sevenfold と同じ「1ファイル複数クリップ」方式。2026-08-08 統一）
//   静的 NPC                           = (1)画像→3D(lowpoly/四角面) → (2)テクスチャ → (5)1 点保存
// 二重課金防止: projects/<PID>/meshy-tasks.json に工程ごとの taskId をキャッシュし、再実行は SUCCEEDED 済みを再利用。
import path from 'node:path';
import {
    meshyConfig, createTask, pollTask, imageFileToDataUri, fetchAssetBytes, downloadToAssets,
    writeBytesToAssets, MeshyError,
} from './meshy-client.mjs';
import { projectPaths, requireProjectDir, readProjectMeta, recordStep, updateProjectMeta } from './story-project.mjs';
import { requireGameDir } from './games.mjs';
import { glbTargetFor } from './story-image-prompts.mjs';
import { mergeEnemyAnimationGlb } from './glb-anim-merge.mjs';
import { callLlmJson } from './llm-client.mjs';
import { readJsonIfExists, writeJson } from './json-utils.mjs';
import { auditLog } from './logger.mjs';
import { inspectGlbSkinning } from './glb-skin-validator.mjs';
import { generateNonHumanoidMotionProfile } from './non-humanoid-motion-profile.mjs';
import { batchCharacterConcurrency } from './story-shared.mjs';
import { mapWithConcurrency } from './async-pool.mjs';
import { proposeNextVersion } from './generators.mjs';

const API = {
    imageTo3d: '/openapi/v1/image-to-3d',
    retexture: '/openapi/v1/retexture',
    rigging: '/openapi/v1/rigging',
    animations: '/openapi/v1/animations',
};

const isPlayable = (role) => role === 'protagonist' || role === 'partner';
export const isHumanoidEnemy = (entry) => entry?.role === 'enemy' && entry?.humanoid === true;
// リグあり扱い＝プレイアブル＋人型敵。ただし Meshy がリギング/アニメを受理できず
// 「リグなし」へ切り替えた敵（riggingUnsupported=true。③実行時に索引へ永続化）は静的経路で扱う。
// プレイアブルは characters.json の 3 点セット前提のためフラグの影響を受けない
export const isAnimatedHumanoid = (entry) => isPlayable(entry?.role)
    || (isHumanoidEnemy(entry) && entry?.riggingUnsupported !== true);

/** キャストの役割から、手動変更と自動生成で共通使用する GLB 保存先を返す。 */
export function glbSlotsForCast(entry, gameId) {
    const cast = { id: entry.castId ?? entry.id, role: entry.role };
    const first = glbTargetFor(cast, gameId).firstPath;
    if (isPlayable(entry.role)) {
        return [
            { slot: 'wait', label: '待機（_wait）', path: first },
            { slot: 'move', label: '移動（_move）', path: first.replace('_wait.glb', '_move.glb') },
            { slot: 'btl', label: 'バトル（_btl）', path: first.replace('_wait.glb', '_btl.glb') },
        ];
    }
    if (entry.role === 'enemy') {
        // 敵は idle/walk の2クリップを持つ統合GLB 1ファイル（2026-08-08 本家方式へ統一）
        return [{ slot: 'model', label: '統合GLB（idle/walk）', path: first }];
    }
    return [{ slot: 'model', label: '静的モデル', path: first }];
}

const toGameAssetRef = (assetPath) => {
    if (assetPath.startsWith('../assets/')) return assetPath;
    if (assetPath.startsWith('assets/')) return `../${assetPath}`;
    throw new Error(`GLB 保存先が assets/ 配下ではありません: ${assetPath}`);
};

/** Meshyで完成したプレイアブル3点セットを、ゲームが読む characters.json へ反映する。 */
export function applyPlayableGlbPathsToCharacters(charactersDocument, entry, gameId) {
    if (!isPlayable(entry?.role)) return { applied: false, changed: false, paths: null };
    const castId = entry.castId ?? entry.id;
    const character = charactersDocument?.characters?.find((candidate) => candidate.id === castId);
    if (!character) return { applied: false, changed: false, paths: null };
    const slots = new Map(glbSlotsForCast(entry, gameId).map((slot) => [slot.slot, toGameAssetRef(slot.path)]));
    const paths = {
        modelPathIdle: slots.get('wait'),
        modelPathWalk: slots.get('move'),
        modelPathAttack: slots.get('btl'),
    };
    const changed = Object.entries(paths).some(([key, value]) => character[key] !== value);
    Object.assign(character, paths);
    return { applied: true, changed, paths };
}

/**
 * 生成済みプレイアブルGLB（③完了分）を characters.json へ再適用する純粋関数。
 * ②のパッケージ再構築（プレビュー初期化）は characters.json を既定モデルで作り直すため、
 * ③を再実行せずとも生成済みモデルが失われないよう、index の記録から復元する。
 * @returns {number} 適用したキャラ数
 */
export function reapplyPlayableGlbPathsCore(charactersDocument, promptIndex, gameId) {
    let applied = 0;
    for (const entry of promptIndex?.prompts ?? []) {
        if (!entry?.modelGeneratedAt || !isPlayable(entry.role)) continue;
        const result = applyPlayableGlbPathsToCharacters(charactersDocument, entry, gameId);
        if (result.applied) applied++;
    }
    return applied;
}

/** プロジェクトの生成記録から characters.json へプレイアブルGLBを復元する（②再構築後の復元用）。 */
export function reapplyPlayableGlbPaths(projectId, gameId) {
    const p = projectPaths(projectId);
    const promptIndex = readJsonIfExists(p.imagePromptsIndex);
    if (!promptIndex) return 0;
    const charactersPath = path.join(requireGameDir(gameId), 'maps/_shared/characters.json');
    const charactersDocument = readJsonIfExists(charactersPath);
    if (!charactersDocument) return 0;
    const applied = reapplyPlayableGlbPathsCore(charactersDocument, promptIndex, gameId);
    if (applied > 0) writeJson(charactersPath, charactersDocument);
    return applied;
}

function bumpPlayerModelCacheVersion(gameDir, gameId) {
    const manifestPath = path.join(gameDir, 'maps/_shared/cache-manifest.json');
    const manifest = readJsonIfExists(manifestPath);
    if (!manifest?.playerModelCache) return false;
    manifest.playerModelCache.version = proposeNextVersion(manifest.playerModelCache.version, gameId, 'core');
    writeJson(manifestPath, manifest);
    return true;
}

function readTaskCache(p) {
    return readJsonIfExists(path.join(p.dir, 'meshy-tasks.json')) ?? { formatVersion: '1.0', casts: {} };
}

function writeTaskCache(p, cache) {
    writeJson(path.join(p.dir, 'meshy-tasks.json'), cache);
}

/** キャッシュ済みタスクの再利用（完了待ち込み。失敗・404 のみ null＝作り直し）。
 *  タイムアウト・429・ネットワーク断など一時エラーではキャッシュを破棄しない
 *  （破棄→新規作成は課金済み工程の丸ごと再課金になるため、エラーとして中断し再実行に委ねる）。 */
async function reuseTask(apiPath, taskId, label, onLog, { reattach = false } = {}) {
    try {
        const task = await pollTask(apiPath, taskId, { onLog, label: `${label}（${reattach ? '前回タスクへ再アタッチ' : 'キャッシュ再利用'}）` });
        onLog(`    ↩ ${label}: 生成済みタスクを再利用（追加消費 P 0・id: ${taskId}）`);
        return task;
    } catch (e) {
        if (e?.code === 'meshy_task_failed' || e?.code === 'meshy_http_404') {
            onLog(`    （${label} のキャッシュ ${taskId} は再利用できない（${e.code}）ため作り直します）`);
            return null;
        }
        throw new MeshyError(
            `${label}: キャッシュ済みタスク ${taskId} の状態確認に失敗しました（${e.message}）。二重課金を避けるため作り直さずに中断します。時間を置いて再実行してください。`,
            { code: e.code ?? 'meshy_error', detail: e },
        );
    }
}

/** wait/btl のアニメーションを、キャストの性格から候補表の中で選ぶ（LLM。失敗時は既定値）。 */
async function pickAnimations(entry, onLog) {
    const anim = meshyConfig().animations;
    const fallback = { waitId: anim.wait.defaultId, btlId: anim.btl.defaultId };
    try {
        const prompt = [
            '次のゲームキャラクターに合う待機アニメと戦闘アニメを、候補から 1つずつ選んで JSON で答えてください。',
            `キャラクター: ${entry.nameJa}（役割: ${entry.role}）`,
            `外見・性格の描写: ${String(entry.promptEn ?? '').slice(0, 300)}`,
            `待機の候補: ${JSON.stringify(anim.wait.candidates)}`,
            `戦闘の候補: ${JSON.stringify(anim.btl.candidates)}`,
            '出力形式（JSON のみ）: {"waitId": <数値>, "btlId": <数値>, "reason": "<一言>"}',
        ].join('\n');
        const r = await callLlmJson('imagePrompt', { prompt, maxTokens: 2048, onLog });
        const waitId = Number(r.json?.waitId);
        const btlId = Number(r.json?.btlId);
        const okWait = anim.wait.candidates.some((c) => c.id === waitId);
        const okBtl = anim.btl.candidates.some((c) => c.id === btlId);
        if (okWait && okBtl) {
            onLog(`    アニメ選定: wait=${waitId} / btl=${btlId}（${String(r.json?.reason ?? '').slice(0, 60)}）`);
            return { waitId, btlId };
        }
    } catch (e) {
        onLog(`    （アニメ選定 LLM が失敗したため既定値を使用: ${e.message?.slice(0, 60)}）`);
    }
    return fallback;
}

/** 人型敵の外見・体格・設定から、最も合う戦闘アニメを候補表内で選ぶ。 */
export function buildEnemyBattleAnimationPrompt(entry, battle) {
    return [
        '次の人型の敵キャラクタに最も合う戦闘アニメを、候補から 1つ選んで JSON で答えてください。',
        `敵: ${entry.nameJa}（${entry.castId}）`,
        `外見・武器・性格・戦い方の描写: ${String(entry.promptEn ?? '').slice(0, 500)}`,
        `見た目プロファイル: ${JSON.stringify(entry.visualProfile ?? {})}`,
        `戦闘アニメ候補: ${JSON.stringify(battle.candidates)}`,
        '武器、体格、戦闘職、攻撃の重さを優先して選ぶこと。',
        '出力形式（JSON のみ）: {"btlId": <数値>, "reason": "<選定理由>"}',
    ].join('\n');
}

async function pickEnemyBattleAnimation(entry, onLog) {
    const battle = meshyConfig().animations.btl;
    try {
        const prompt = buildEnemyBattleAnimationPrompt(entry, battle);
        const r = await callLlmJson('imagePrompt', { prompt, maxTokens: 2048, onLog });
        const btlId = Number(r.json?.btlId);
        if (battle.candidates.some((candidate) => candidate.id === btlId)) {
            onLog(`    人型敵の戦闘アニメ選定: btl=${btlId}（${String(r.json?.reason ?? '').slice(0, 80)}）`);
            return btlId;
        }
    } catch (error) {
        onLog(`    （人型敵の戦闘アニメ選定が失敗したため既定値を使用: ${error.message?.slice(0, 60)}）`);
    }
    return battle.defaultId;
}

/**
 * Meshyリギング用の物理身長（m）を解決する純粋関数。
 * 優先順: 安定リギング用 fixedHeightMeters → 専用値 riggingHeightMeters → collider.height → 設定既定値。
 * displayHeight はSevenfold内の表示スケールで単位が異なるため、候補へ含めない。
 */
export function resolveRiggingHeight(character, riggingConfig = {}) {
    const configuredMin = Number(riggingConfig.minHeightMeters);
    const configuredMax = Number(riggingConfig.maxHeightMeters);
    const min = Number.isFinite(configuredMin) && configuredMin > 0 ? configuredMin : 0.5;
    const max = Number.isFinite(configuredMax) && configuredMax > min ? configuredMax : 3.0;
    const configuredDefault = Number(riggingConfig.defaultHeightMeters);
    const fallback = Number.isFinite(configuredDefault) && configuredDefault >= min && configuredDefault <= max
        ? configuredDefault : Math.min(max, Math.max(min, 1.7));
    const rejected = [];
    const fixedHeight = Number(riggingConfig.fixedHeightMeters);
    if (Number.isFinite(fixedHeight) && fixedHeight >= min && fixedHeight <= max) {
        return { height: fixedHeight, source: 'fixedHeightMeters', rejected };
    }
    if (riggingConfig.fixedHeightMeters != null && riggingConfig.fixedHeightMeters !== '') {
        rejected.push({ source: 'fixedHeightMeters', value: riggingConfig.fixedHeightMeters });
    }
    const candidates = [
        ['riggingHeightMeters', character?.riggingHeightMeters],
        ['collider.height', character?.collider?.height],
    ];
    for (const [source, raw] of candidates) {
        if (raw == null || raw === '') continue;
        const height = Number(raw);
        if (Number.isFinite(height) && height >= min && height <= max) return { height, source, rejected };
        rejected.push({ source, value: raw });
    }
    return { height: fallback, source: 'defaultHeightMeters', rejected };
}

/** ゲームのキャラ設定からMeshy用物理身長を解決する。 */
function heightFor(castId, gameId) {
    let character = null;
    try {
        const chars = readJsonIfExists(path.join(requireGameDir(gameId), 'maps/_shared/characters.json'));
        character = (chars?.characters ?? []).find((x) => x.id === castId) ?? null;
    } catch { /* ゲーム未生成・キャラ未登録は設定既定値へ */ }
    return resolveRiggingHeight(character, meshyConfig().rigging);
}

/** GLB 2.0 コンテナの最小ヘッダー検証。不正なバイナリを assets/ へ書かない。 */
export function validateUploadedGlb(bytes, maxBytes = 25 * 1024 * 1024) {
    const data = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    if (data.byteLength === 0) throw new Error('GLB データが空です');
    if (data.byteLength > maxBytes) {
        throw new Error(`GLB が大きすぎます（${Math.ceil(data.byteLength / 1024 / 1024)}MB。上限 ${Math.floor(maxBytes / 1024 / 1024)}MB）`);
    }
    if (data.byteLength < 20) throw new Error('GLB ヘッダーが不正です');
    const magic = String.fromCharCode(data[0], data[1], data[2], data[3]);
    const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    const version = view.getUint32(4, true);
    const declaredLength = view.getUint32(8, true);
    const firstChunkLength = view.getUint32(12, true);
    const firstChunkType = view.getUint32(16, true);
    const hasJsonChunk = firstChunkType === 0x4E4F534A && firstChunkLength > 0 && 20 + firstChunkLength <= data.byteLength;
    if (magic !== 'glTF' || version !== 2 || declaredLength !== data.byteLength || !hasJsonChunk) {
        throw new Error('GLB 2.0 形式のファイルを指定してください');
    }
    return { version, bytes: data.byteLength, jsonChunkBytes: firstChunkLength };
}

/**
 * 「GLB変更」用。キャストの役割で許可されたスロットだけを計算済みパスへ保存する。
 */
export async function saveUploadedGlb(projectId, castId, slot, { dataBase64, fileName = null } = {}) {
    requireProjectDir(projectId);
    const p = projectPaths(projectId);
    const meta = readProjectMeta(projectId);
    if (!meta.gameId) throw new Error('ゲームが未リンクです（先に ② ベースデータ生成を実行してください）');
    const idx = readJsonIfExists(p.imagePromptsIndex);
    const entry = idx?.prompts?.find((candidate) => candidate.castId === castId);
    if (!entry) throw new Error(`キャストが見つかりません: ${castId}`);
    const target = glbSlotsForCast(entry, meta.gameId).find((candidate) => candidate.slot === slot);
    if (!target) throw new Error(`このキャストで変更できない GLB 種別です: ${slot}`);
    if (typeof dataBase64 !== 'string' || dataBase64.trim() === '') throw new Error('GLB データ（base64）が空です');
    const rawBase64 = dataBase64.replace(/^data:[^;]+;base64,/, '');
    const bytes = Buffer.from(rawBase64, 'base64');
    const maxBytes = Number(meshyConfig().manualUpload?.maxBytes) || 25 * 1024 * 1024;
    const validation = validateUploadedGlb(bytes, maxBytes);
    await writeBytesToAssets(bytes, target.path);
    const uploadedAt = new Date().toISOString();
    const manualGlbUploads = {
        ...(entry.manualGlbUploads ?? {}),
        [slot]: {
            path: target.path,
            uploadedAt,
            bytes: bytes.length,
            ...(fileName ? { originalName: String(fileName).slice(0, 120) } : {}),
        },
    };
    idx.prompts = idx.prompts.map((candidate) => candidate.castId === castId
        ? { ...candidate, manualGlbUploads, modelGeneratedAt: uploadedAt }
        : candidate);
    writeJson(p.imagePromptsIndex, idx);
    auditLog('story-cast-glb-upload', { projectId, castId, slot, path: target.path, bytes: bytes.length });
    return { ok: true, castId, slot, path: target.path, bytes: bytes.length, validation };
}

/**
 * 3D キャラを自動生成する（一括実行時は設定上限まで並列。1 体の失敗では止めず末尾で集計）。
 * @param {object} opts { castIds?: 対象 castId 配列, batch?: 一括実行の並列化を有効にする, onLog, checkpoint?: 工程境界での永続化コールバック
 *   （ブラウザ版が IndexedDB へ flush するために使う。taskId 記録直後に呼ぶことで、タブクラッシュ後も
 *   課金済み Meshy タスクへ再アタッチでき二重課金を防ぐ。Node 版は fs 直書きのため不要＝未指定） }
 */
export async function runModelGenerate(projectId, { castIds = null, batch = false, onLog = () => {}, signal = null, checkpoint = null } = {}) {
    requireProjectDir(projectId);
    const p = projectPaths(projectId);
    const cfg = meshyConfig();
    const meta = readProjectMeta(projectId);
    const gameId = meta.gameId;
    if (!gameId) throw new Error('ゲームが未リンクです（先に ② ベースデータ生成を実行してください）。');
    const gameDir = requireGameDir(gameId);
    const idx = readJsonIfExists(p.imagePromptsIndex);
    if (!idx || !Array.isArray(idx.prompts) || idx.prompts.length === 0) {
        throw new Error('画像プロンプトが未生成です。先に「③-1 画像生成プロンプト」を実行してください。');
    }
    const targets = idx.prompts.filter((e) => (!castIds || castIds.includes(e.castId)));
    if (targets.length === 0) throw new Error('対象キャストがありません（castIds を確認してください）。');

    const cache = readTaskCache(p);
    const generated = [];
    const failures = [];
    const castLogs = {};
    let creditsTotal = 0;
    const targetOrder = new Map(targets.map((entry, index) => [entry.castId, index]));
    const concurrency = batchCharacterConcurrency('modelGeneration', batch);
    let playableModelsApplied = 0;
    // ブラウザ版の IndexedDB flush が完了順逆転で古いスナップショットを上書きしないよう、
    // キャスト処理は並列でも checkpoint 自体は呼び出し順に直列化する。
    let checkpointQueue = Promise.resolve();
    const persistCheckpoint = () => {
        if (!checkpoint) return Promise.resolve();
        const pending = checkpointQueue.then(() => checkpoint());
        checkpointQueue = pending.catch(() => {});
        return pending;
    };

    await mapWithConcurrency(targets, async (entry) => {
        const castId = entry.castId;
        const castLines = [];
        castLogs[castId] = castLines;
        const castLog = (line) => {
            castLines.push(String(line));
            onLog(line);
        };
        let castCredits = 0; // このキャスト単体の消費 P（creditsTotal は全体累積）
        let motionProfile = entry.motionProfile ?? null;
        let motionProfilePath = entry.motionProfilePath ?? null;
        let riggingFallbackReason = null; // 人型敵を「リグなし」へ切り替えたときの理由（索引へ永続化）
        let generatedCast = null;
        try {
            if (!entry.imageFile) {
                throw new Error('キャスト画像が未生成です（③ キャラ生成の「画像を自動生成」または「画像変更」で登録してください）');
            }
            const playable = isPlayable(entry.role);
            const humanoidEnemy = isHumanoidEnemy(entry);
            const animatedHumanoid = isAnimatedHumanoid(entry);
            const generationLabel = playable
                ? 'プレイアブル: 3D→テクスチャ→リグ→アニメ×3'
                : animatedHumanoid
                    ? '人型敵: 3D→テクスチャ→リグ→アニメ×2'
                    : entry.role === 'enemy'
                        ? (humanoidEnemy
                            ? 'リグなし人型敵: 静的3D→テクスチャ→手続き型モーション設定'
                            : '非人型敵: 静的3D→テクスチャ→手続き型モーション設定')
                        : '静的: 3D→テクスチャ';
            castLog(`  ── ${entry.nameJa}（${castId} / ${generationLabel}）`);
            const imageAbs = path.join(p.imagesDir, entry.imageFile);
            const dataUri = imageFileToDataUri(imageAbs);

            // キャッシュ（画像を差し替えたら無効化）
            let cc = cache.casts[castId];
            if (!cc || cc.imageGeneratedAt !== (entry.imageGeneratedAt ?? null)) {
                cc = { imageGeneratedAt: entry.imageGeneratedAt ?? null, stages: {} };
                cache.casts[castId] = cc;
            }
            // sig = 設定・上流タスクの署名。変わったらキャッシュを破棄して作り直す
            //（例: プリセットを standard→lowpoly に変更、上流の 3D を作り直した等。二重課金防止と正しさの両立）
            const runStage = async (stageKey, apiPath, body, label, sig = '') => {
                const cached = cc.stages[stageKey];
                if (cached?.taskId && (cached.sig ?? '') === sig) {
                    // succeeded は消費 P 0 の再利用。running は前回のタイムアウト/切断の残骸＝
                    // Meshy 側で完了している可能性が高いので、新規作成せず同じタスクへ再アタッチする
                    const reattach = cached.status === 'running';
                    const task = await reuseTask(apiPath, cached.taskId, label, castLog, { reattach });
                    if (task) {
                        cc.stages[stageKey] = { ...cached, status: 'succeeded' };
                        writeTaskCache(p, cache);
                        const credits = reattach ? Number(task?.consumed_credits ?? 0) : 0;
                        if (credits > 0) {
                            creditsTotal += credits;
                            castCredits += credits;
                            castLog(`    🧊 Meshy 消費: ${credits} P（${label}・前回作成分）`);
                        }
                        return { taskId: cached.taskId, task, credits };
                    }
                    delete cc.stages[stageKey];
                } else if (cached?.taskId) {
                    castLog(`    （${label}: 設定変更を検出したためキャッシュを破棄して作り直します）`);
                    delete cc.stages[stageKey];
                }
                const taskId = await createTask(apiPath, body);
                castLog(`    Meshy タスク作成: ${label}（id: ${taskId}）`);
                // 作成した瞬間に taskId を永続化する。ポーリングがタイムアウト・切断で失敗しても
                // 再実行時に同じタスクへ再アタッチでき、課金済みタスクの作り直し（二重課金）を防ぐ
                cc.stages[stageKey] = { taskId, sig, status: 'running', at: new Date().toISOString() };
                writeTaskCache(p, cache);
                await persistCheckpoint();
                const task = await pollTask(apiPath, taskId, { onLog: castLog, label });
                const credits = Number(task?.consumed_credits ?? 0);
                auditLog('meshy-task', { apiPath, taskId, label, ok: true, credits });
                if (credits > 0) castLog(`    🧊 Meshy 消費: ${credits} P（${label}）`);
                cc.stages[stageKey] = { taskId, sig, status: 'succeeded', at: new Date().toISOString() };
                writeTaskCache(p, cache);
                await persistCheckpoint();
                creditsTotal += credits;
                castCredits += credits;
                return { taskId, task, credits };
            };

            // (1) 画像→3D（テクスチャ無し）
            const presetName = humanoidEnemy
                ? cfg.imageTo3d.humanoidEnemyPreset
                : (cfg.imageTo3d.presetByRole[entry.role] ?? 'lowpoly');
            const preset = cfg.imageTo3d.presets[presetName];
            const t1 = await runStage('imageTo3d', API.imageTo3d,
                { ...preset, image_url: dataUri, target_formats: ['glb'] },
                `(1) 画像→3D [${presetName}]`, `preset:${presetName}:${JSON.stringify(preset)}`);

            // (2) テクスチャ（スタイル = キャスト画像）
            const t2 = await runStage('retexture', API.retexture,
                { input_task_id: t1.taskId, ...cfg.retexture, image_style_url: dataUri, target_formats: ['glb'] },
                '(2) テクスチャ', `t1:${t1.taskId}`);
            const texturedGlbUrl = t2.task?.model_urls?.glb;
            if (!texturedGlbUrl) throw new MeshyError('(2) の結果に GLB URL がありません', { detail: t2.task });

            const castLike = { id: castId, role: entry.role };
            // 静的モデルとして保存する共通処理。NPC・非人型敵の通常経路に加え、
            // 人型敵で Meshy がリギング/アニメを受理できない場合のフォールバックからも使う
            //（(1)(2)で作成済みのテクスチャ付きGLBをそのまま利用し、追加課金を発生させない）
            const saveStaticCast = async () => {
                // (5) 保存（保存先は現 gameId から再計算）
                //  - NPC: 静的 1 点
                //  - 敵: 統合GLB 1 点（エンジンの modelPathIdle / modelPathWalk が同一ファイルを参照）。
                //    リグ無しのためクリップは持たず、動きは手続き型モーションが担う。
                const dest = glbTargetFor(castLike, gameId).firstPath;
                const saved = await downloadToAssets(texturedGlbUrl, dest, { onLog: castLog });
                const files = [saved.path];
                if (entry.role === 'enemy') {
                    // リグ無しの敵は Meshy の Rigging / Animation API を使用しない。
                    // ゲーム側で静的 GLB に手続き型モーションを適用できるよう、分類・数値を
                    // 自己完結した JSON として GLB の隣へ保存し、プロンプト索引にも保持する。
                    motionProfile = await generateNonHumanoidMotionProfile(entry, { onLog: castLog });
                    const motionDest = dest.replace(/\.glb$/, '.motion.json');
                    await writeBytesToAssets(
                        Buffer.from(`${JSON.stringify(motionProfile, null, 2)}\n`, 'utf-8'),
                        motionDest,
                    );
                    motionProfilePath = motionDest;
                    files.push(motionDest);
                }
                generatedCast = { castId, files, credits: castCredits };
                generated.push(generatedCast);
            };
            if (!animatedHumanoid) {
                await saveStaticCast();
            } else {
                try {
                    // (3) リギング（ヒューマノイド）
                    const resolvedHeight = heightFor(castId, gameId);
                    const height = resolvedHeight.height;
                    for (const rejected of resolvedHeight.rejected) {
                        castLog(`    ⚠ リギング身長 ${rejected.source}=${rejected.value} は許容範囲外のため使用しません`);
                    }
                    castLog(`    リギング物理身長: ${height}m（${resolvedHeight.source}）`);
                    const t3 = await runStage('rigging', API.rigging,
                        { model_url: texturedGlbUrl, height_meters: height },
                        `(3) リギング（身長 ${height}m）`, `t2:${t2.taskId}:h:${height}`);

                    // Meshyがタスク成功を返しても、極端な身長指定等で全頂点が数本の胴体ボーンだけへ
                    // バインドされた壊れたGLBが返る場合がある。保存前に実ウェイト分布を検査する。
                    const validatePlayableGlb = (buf) => {
                        const quality = inspectGlbSkinning(buf, cfg.rigging);
                        if (!quality.ok) {
                            throw new MeshyError(
                                `生成されたGLBのスキンウェイトが不正です: ${quality.reason}`,
                                { code: 'meshy_skin_validation_failed', detail: quality },
                            );
                        }
                        castLog(`    ✔ スキン検査: 使用ボーン ${quality.usedJointCount}/${quality.jointCount}本・頂点 ${quality.weightedVertexCount}/${quality.totalVertexCount}`);
                        return quality;
                    };
                    const invalidateRiggingCache = async () => {
                        delete cc.stages.rigging;
                        for (const key of Object.keys(cc.stages)) {
                            if (key.startsWith('anim_')) delete cc.stages[key];
                        }
                        writeTaskCache(p, cache);
                        await persistCheckpoint();
                        castLog('    ⚠ 不正なスキンを検出したため、次回実行でリギング以降を作り直します');
                    };

                    // リギング結果そのものを先に検査し、壊れたリグへアニメを追加課金しない。
                    const riggedGlbUrl = t3.task?.result?.rigged_character_glb_url;
                    if (!riggedGlbUrl) {
                        throw new MeshyError('(3) の結果にリギング済みGLB URLがありません', { detail: t3.task });
                    }
                    try {
                        validatePlayableGlb(await fetchAssetBytes(riggedGlbUrl));
                    } catch (error) {
                        if (error?.code === 'meshy_skin_validation_failed') await invalidateRiggingCache();
                        throw error;
                    }

                    // (4) アニメ（プレイアブル=3本、人型敵=2本）。
                    // 人型敵の btl は外見・武器・体格・戦闘特性から LLM が候補内で選ぶ。
                    const anim = cfg.animations;
                    const slots = new Map(glbSlotsForCast(entry, gameId).map((slot) => [slot.slot, slot]));
                    let plan;
                    if (playable) {
                        const picked = await pickAnimations(entry, castLog);
                        plan = [
                            { key: 'anim_wait', actionId: picked.waitId, dest: slots.get('wait').path, label: '_wait（待機）' },
                            { key: 'anim_move', actionId: anim.move.defaultId, dest: slots.get('move').path, label: '_move（走る）' },
                            { key: 'anim_btl', actionId: picked.btlId, dest: slots.get('btl').path, label: '_btl（戦闘）' },
                        ];
                    } else {
                        // 敵はアニメ2本を idle/walk の2クリップへ統合し、1ファイルだけを保存する
                        // （本家 sevenfold と同方式）。runStage のキャッシュキーは従来の
                        // anim_move/anim_btl のまま＝旧形式時代の課金済み Meshy タスクを再利用できる。
                        const enemyBattleId = await pickEnemyBattleAnimation(entry, castLog);
                        plan = [
                            { key: 'anim_move', actionId: anim.move.defaultId, clip: 'walk', label: '敵移動（walk クリップ）' },
                            { key: 'anim_btl', actionId: enemyBattleId, clip: 'idle', label: '敵戦闘（idle クリップ）' },
                        ];
                    }
                    const files = [];
                    const enemyAnimBytes = {};
                    for (const a of plan) {
                        const t4 = await runStage(`${a.key}_${a.actionId}`, API.animations,
                            { rig_task_id: t3.taskId, action_id: a.actionId },
                            `(4) アニメ ${a.label} action_id=${a.actionId}`, `t3:${t3.taskId}`);
                        const url = t4.task?.result?.animation_glb_url ?? t4.task?.animation_glb_url;
                        if (!url) throw new MeshyError(`(4) ${a.label} の結果に GLB URL がありません`, { detail: t4.task });
                        try {
                            if (a.dest) {
                                const saved = await downloadToAssets(url, a.dest, { onLog: castLog, validate: validatePlayableGlb });
                                files.push(saved.path);
                            } else {
                                const bytes = await fetchAssetBytes(url);
                                validatePlayableGlb(Buffer.from(bytes));
                                enemyAnimBytes[a.clip] = bytes;
                            }
                        } catch (error) {
                            if (error?.code === 'meshy_skin_validation_failed') await invalidateRiggingCache();
                            throw error;
                        }
                    }
                    if (!playable) {
                        const merged = await mergeEnemyAnimationGlb(enemyAnimBytes.idle, enemyAnimBytes.walk, { onLog: castLog });
                        castLog(`    ✔ アニメ2本を統合GLBへ結合（walk=${merged.walkMode === 'static' ? '静止代替' : 'リターゲット'} / ${merged.channels}チャンネル）`);
                        const savedMerged = await writeBytesToAssets(merged.bytes, slots.get('model').path, { onLog: castLog });
                        files.push(savedMerged.path);
                    }
                    if (playable) {
                        const charactersPath = path.join(gameDir, 'maps/_shared/characters.json');
                        const charactersDocument = readJsonIfExists(charactersPath);
                        const applied = applyPlayableGlbPathsToCharacters(charactersDocument, entry, gameId);
                        if (!applied.applied) {
                            throw new Error(`キャラ ID「${castId}」が characters.json にありません。`);
                        }
                        writeJson(charactersPath, charactersDocument);
                        playableModelsApplied += 1;
                    }
                    generatedCast = { castId, files, credits: castCredits };
                    generated.push(generatedCast);
                } catch (error) {
                    // プレイアブルはリグ必須（characters.json の wait/move/btl 3点セット前提）の
                    // ため、フォールバックせず従来どおり失敗として扱う
                    if (playable) throw error;
                    // 敵・NPC: Meshy がリギング/アニメーションを受理できないモデル（例: 重装甲や
                    // 武器一体化で人型判定に落ちる）は「リグなし」へ切り替え、(2)までの静的GLBを
                    // そのまま利用する。切替はプロンプト索引へ永続化し、以後の再実行も静的経路で
                    // 処理する（画像を差し替えたときだけ解除され、リギングへ再挑戦する）
                    riggingFallbackReason = String(error?.message ?? error).slice(0, 300);
                    castLog('    ⚠ リギング/アニメ生成に失敗したため「リグなし」（静的GLB＋手続き型モーション）へ切り替えます');
                    castLog(`      理由: ${riggingFallbackReason}`);
                    await saveStaticCast();
                }
            }

            // index.json の推奨配置先を現 gameId に更新（解析時の旧 gameId で焼かれたパスのずれを修正）
            const glb = glbTargetFor(castLike, gameId);
            const completedAt = new Date().toISOString();
            idx.prompts = idx.prompts.map((e) => (e.castId === castId
                ? {
                    ...e,
                    glbTarget: glb.text,
                    glbPath: glb.firstPath,
                    ...(motionProfile ? { motionProfile } : {}),
                    ...(motionProfilePath ? { motionProfilePath } : {}),
                    ...(riggingFallbackReason ? { riggingUnsupported: true, riggingUnsupportedReason: riggingFallbackReason } : {}),
                    modelGeneratedAt: completedAt,
                    ...(playable ? { modelAppliedAt: completedAt } : {}),
                }
                : e));
            writeJson(p.imagePromptsIndex, idx);
            await persistCheckpoint(); // キャスト完了ごとに永続化（ダウンロード済み GLB と index を確定）
            castLog(`  ✔ ${castId}: 生成物 ${generatedCast.files.length}点を配置しました`);
        } catch (e) {
            failures.push({ castId, code: e.code ?? 'error', message: e.message });
            castLog(`  ✖ ${castId}: ${e.message}`);
        }
    }, { concurrency, signal });
    generated.sort((a, b) => targetOrder.get(a.castId) - targetOrder.get(b.castId));
    failures.sort((a, b) => targetOrder.get(a.castId) - targetOrder.get(b.castId));

    if (playableModelsApplied > 0) {
        bumpPlayerModelCacheVersion(gameDir, gameId);
        updateProjectMeta(projectId, (projectMeta) => ({
            ...projectMeta,
            previewRev: (projectMeta.previewRev ?? 0) + 1,
        }));
        await persistCheckpoint();
    }

    recordStep(projectId, 'castModels', { ok: failures.length === 0, generated: generated.length, failed: failures.length, credits: creditsTotal });
    auditLog('story-cast-models', { projectId, generated: generated.length, failed: failures.length, credits: creditsTotal });
    onLog(`✔ 3D 自動生成が完了: 成功 ${generated.length} / 失敗 ${failures.length} / 🧊 Meshy 消費合計 ${creditsTotal} P`);
    return { ok: failures.length === 0, generated, failures, creditsTotal, castLogs };
}
